import { ROLES_RESTRICTED_TO_SUPPORTED_COMPANIES } from '@constants';
import {
  CandidateVerificationEmployerDecision,
  CandidateVerificationResult,
  CandidateVerificationStatus,
  ChangeRoutePayload,
  DocumentsSigningMode,
  InAppNotification,
  InAppNotificationType,
  ROLES_KEYS,
  User,
  UserProcessStatus,
} from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { escape } from 'lodash-es';
import { truncateText } from '@shared/utils/truncate-text';
import {
  cvmCandidateVerificationAutoReadTypes,
  getNotificationDefinition,
  managerCandidateVerificationAutoReadTypes,
} from './notifications.registry';

const foreignerSeeSummaryWithStatuses = [
  UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,
  UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,

  UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
  UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
  UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

  UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
  UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,
  UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,

  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,

  UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

  UserProcessStatus.DECISION_ISSUED,
];

export { cvmCandidateVerificationAutoReadTypes, managerCandidateVerificationAutoReadTypes };

export const setNotificationViewOpts = (
  notification: InAppNotification,
  myself: Partial<User>,
  translateService: TranslateService
): InAppNotification => {
  const iamForeigner = myself.role.key === ROLES_KEYS.Foreigner;
  const iShouldBeRestrictedBySupportedCompanies = ROLES_RESTRICTED_TO_SUPPORTED_COMPANIES.includes(
    myself.role.key as ROLES_KEYS
  );
  const definition = getNotificationDefinition(notification.type);

  let clickable = true;
  if (iamForeigner) {
    clickable = canForeignerClickNotification(notification);
  }
  if (iShouldBeRestrictedBySupportedCompanies) {
    clickable = canEmployeeClickNotification({ myself, notification });
  }
  if (definition?.clickableOverride === false) {
    clickable = false;
  }

  const foreignerName = getForeignerName(notification);
  const { signingMode, signingDate } = getDocsSigningDataFormatted(notification, translateService);
  const candidateVerificationAdditionalInfo = getCandidateVerificationAdditionalInfo(notification, translateService);
  const workPermitAdditionalInfo = getWorkPermitAdditionalInfo(notification, translateService);
  const linkParams = getNotificationRoute(notification, myself, clickable);

  let processCategoryTranslated = null;
  if (notification.data.additionalInfo?.processCategory) {
    processCategoryTranslated = translateService.instant(
      'NOTIFICATION_PROCESS_CATEGORY.' + notification.data.additionalInfo.processCategory
    );
  }

  let documentName = '';
  if (notification.relatedUserDocument) {
    documentName = getDocumentName(notification, translateService);
  }

  let origError = '';
  if (notification.data.additionalInfo?.origError) {
    origError = notification.data.additionalInfo?.origError;
  }

  if (!origError && notification.data.additionalInfo.error) {
    origError = notification.data.additionalInfo.error;
  }

  if (!definition) {
    console.warn(`Missing notification configurator for ${notification.type}`);
  }

  return {
    ...notification,
    foreignerName,
    documentName,
    clickable,
    linkParams,
    icons: definition?.icons || [],
    origError,
    data: {
      ...notification.data,
      additionalInfo: {
        ...(notification.data?.additionalInfo || {}),
        ...candidateVerificationAdditionalInfo,
        ...workPermitAdditionalInfo,
        signingDate,
        signingMode,
        processCategoryTranslated,
        visitAddress: notification.data?.additionalInfo?.visitAddress || 'missing_address',
      },
    },
  };
};

const canEmployeeClickNotification = ({
  myself,
  notification,
}: {
  myself: Partial<User>;
  notification: InAppNotification;
}): boolean => {
  const companyId = notification?.relatedForeigner?.companyId || notification?.relatedCandidateVerification?.companyId;

  if (!companyId) {
    return true;
  }

  const isAllowedToEdit = !!myself.supportingCompanies.find(sc => sc.canEditProcesses && sc.companyId === companyId);
  return isAllowedToEdit;
};

const canForeignerClickNotification = (notification: InAppNotification): boolean => {
  if (!notification?.relatedUserProcess?.status) {
    return true;
  }

  const userProcessStatus = notification.relatedUserProcess.status;
  const userSeeSummaryInProcess = foreignerSeeSummaryWithStatuses.includes(userProcessStatus);

  if (userSeeSummaryInProcess) {
    const unclickableNotificationTypes = [
      InAppNotificationType.EMPLOYEE_ADDED_DOCUMENT_COMMENT,
      InAppNotificationType.DOCUMENTS_LIST_CHANGED,
    ];
    return !unclickableNotificationTypes.includes(notification.type);
  }

  const unclickableNotificationTypes = [
    InAppNotificationType.NEW_FINAL_VERIFICATION_COMMENTS,
    InAppNotificationType.CHANGED_FINAL_VERIFICATION_COMMENTS,
  ];
  return !unclickableNotificationTypes.includes(notification.type);
};

export const getDocumentName = (notification: InAppNotification, translateService: TranslateService): string => {
  const doc = notification.relatedUserDocument?.document;
  if (!doc?.name[translateService.currentLang]) {
    return '';
  }
  return truncateText(doc.name[translateService.currentLang], 50);
};

export const getDocsSigningDataFormatted = (
  notification: InAppNotification,
  translateService: TranslateService
): { signingMode: string; signingDate: string } => {
  const opts: { signingMode: string; signingDate: string } = {
    signingMode: null,
    signingDate: null,
  };

  const sMode = notification.data?.additionalInfo?.newMode;
  if (sMode) {
    opts.signingMode = translateService.instant(`MANAGEMENT.SCHEDULED_VISITS_LIST.MODE_${sMode}`);
  }
  if (notification.data?.additionalInfo?.newDate) {
    opts.signingDate = moment(notification.data.additionalInfo.newDate).format('DD-MM-YYYY HH:mm');
  }
  if (
    !notification.data?.additionalInfo?.newDate &&
    notification.data?.additionalInfo?.oldDate &&
    sMode === DocumentsSigningMode.SENDING_BY_POST
  ) {
    opts.signingDate = moment(notification.data.additionalInfo.oldDate).format('DD-MM-YYYY HH:mm');
  }
  return opts;
};

const getCandidateVerificationAdditionalInfo = (
  notification: InAppNotification,
  translateService: TranslateService
): Record<string, string | null> => {
  const result =
    (notification.data?.additionalInfo?.result as CandidateVerificationResult | null | undefined) ||
    notification.relatedCandidateVerification?.result;
  const status =
    (notification.data?.additionalInfo?.status as CandidateVerificationStatus | null | undefined) ||
    notification.relatedCandidateVerification?.status;
  const decision = notification.data?.additionalInfo?.decision as CandidateVerificationEmployerDecision | null;
  const deadline = notification.data?.additionalInfo?.newValidUntil as string | null | undefined;

  return {
    resultLabel: result ? translateService.instant(`CANDIDATE_VERIFICATION.RESULT.${result}`) : null,
    statusLabel: status ? translateService.instant(`CANDIDATE_VERIFICATION.STATUS.${status}`) : null,
    decisionLabel: getCandidateVerificationDecisionLabel(decision, translateService),
    deadlineLabel: deadline ? moment(deadline).format('DD-MM-YYYY HH:mm') : null,
  };
};

const getWorkPermitAdditionalInfo = (
  notification: InAppNotification,
  translateService: TranslateService
): Record<string, string | null> => {
  const documentType = notification.data?.additionalInfo?.documentType as string | undefined;
  const workPermitStatus = notification.data?.additionalInfo?.workPermitStatus as string | undefined;
  const employeeName = notification.data?.additionalInfo?.employeeName as string | undefined;

  return {
    // Free text typed by managers/consultants, rendered via [innerHTML] without sanitizing.
    employeeName: employeeName ? escape(employeeName) : null,
    workPermitDocumentTypeLabel: documentType
      ? translateService.instant(`MY_WORK_PERMITS.DOCUMENT_TYPE.${documentType}`)
      : null,
    workPermitStatusLabel: workPermitStatus
      ? translateService.instant(`MY_WORK_PERMITS.STATUS.${workPermitStatus}`)
      : null,
  };
};

const getCandidateVerificationDecisionLabel = (
  decision: CandidateVerificationEmployerDecision | null,
  translateService: TranslateService
): string | null => {
  if (decision === CandidateVerificationEmployerDecision.ACCEPT) {
    return translateService.instant('IN_APP_NOTIFICATIONS.EMPLOYMENT_CONFIRMED_LABEL');
  }

  if (decision === CandidateVerificationEmployerDecision.REJECT) {
    return translateService.instant('IN_APP_NOTIFICATIONS.CANDIDATE_REJECTED_LABEL');
  }

  return null;
};

export const getForeignerName = (notification: InAppNotification): string => {
  let foreignerName = notification.data?.additionalInfo?.foreignerName;

  if (notification.relatedForeigner) {
    foreignerName = notification.relatedForeigner.name || notification.relatedForeigner.email;
  }

  if (notification.relatedUserProcess?.personalDetails) {
    const { personalDetails } = notification.relatedUserProcess;
    if (personalDetails.name) {
      foreignerName = personalDetails.name;
      if (personalDetails.surname) {
        foreignerName = `${foreignerName} ${personalDetails.surname}`;
      }
    }
  }
  const name = foreignerName || '';
  return truncateText(name, 20, false, '...');
};

export const getNotificationRoute = (
  notification: InAppNotification,
  myself: Partial<User>,
  clickable = true
): ChangeRoutePayload | null => {
  if (!clickable) {
    return null;
  }

  return getNotificationDefinition(notification.type)?.getRoute?.(notification, myself) || null;
};
